import { Component, ElementRef, Input, OnChanges } from "@angular/core";
import { Objective } from "@common/ADAPT.Common.Model/organisation/objective";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import ArrayStore from "devextreme/data/array_store";
import DataSource from "devextreme/data/data_source";
import { BehaviorSubject, filter, of, switchMap } from "rxjs";
import { ObjectivesService } from "../objectives.service";

@Component({
    selector: "adapt-select-supporting-objective",
    templateUrl: "./select-supporting-objective.component.html",
    styleUrl: "./select-supporting-objective.component.scss",
    standalone: false,
})
export class SelectSupportingObjectiveComponent extends BaseComponent implements OnChanges {
    @Input() public objective!: Objective;
    public dataSource?: DataSource;

    private triggerUpdate$ = new BehaviorSubject<void>(undefined);

    public constructor(
        private objectivesService: ObjectivesService,
        elementRef: ElementRef,
        rxjsBreezeService: RxjsBreezeService,
        dialogService: AdaptCommonDialogService,
    ) {
        super(elementRef);

        rxjsBreezeService.entityTypeChanged(Objective).pipe(
            filter((objective) => objective.entityAspect.entityState.isDeletedOrDetached() || objective.entityAspect.entityState.isAdded()),
            switchMap((objective) => {
                if (this.objective.parentObjectiveId === objective.objectiveId) {
                    // only deleted here as newly added won't be parent
                    this.objective.parentObjectiveId = undefined;

                    return dialogService.showMessageDialog(
                        "Supporting Objective Deleted",
                        "The supporting objective you selected has been deleted and removed from this objective.",
                        "OK",
                    );
                }

                return of(objective);
            }),
            this.takeUntilDestroyed(),
        ).subscribe(() => this.triggerUpdate$.next());
    }

    public ngOnChanges() {
        this.triggerUpdate$.pipe(
            switchMap(() => this.objectivesService.getUnclosedPotentialSupportiveObjectives(this.objective)),
            this.takeUntilDestroyed(),
        ).subscribe((objectives) => {
            if (this.objective.parentObjective && objectives.indexOf(this.objective.parentObjective) < 0) {
                objectives.push(this.objective.parentObjective);
            }

            const filtered = objectives.filter((obj) => obj !== this.objective);
            this.dataSource = new DataSource({
                store: new ArrayStore({
                    data: filtered,
                    key: "objectiveId",
                }),
                group: (obj: Objective) => this.getObjectiveGroup(obj),
                postProcess: (data: any[]) => this.postProcessObjectives(data),
            });
        });
    }

    private getObjectiveGroup(obj: Objective): string {
        if (!obj.teamId) {
            return "__organisation__";
        }
        if (obj.teamId === this.objective.teamId) {
            return "__thisTeam__";
        }
        return obj.team?.name ?? `Team ${obj.teamId}`;
    }

    private postProcessObjectives(data: any[]): any[] {
        const sortOrderMap: Record<string, number> = {
            __thisTeam__: 1,
            __organisation__: 2,
        };

        for (const row of data) {
            row.sortOrder = sortOrderMap[row.key] ?? 3;
        }

        return data.sort((a: any, b: any) => a.sortOrder - b.sortOrder);
    }
}
